// @flow
import { toastError } from 'bench-internal-theme';
import { Mutation, Query } from '@apollo/client/react/components';
import React from 'react';

import openCreateOrEditPersonModal from './openCreateOrEditPersonModal';
import type { PersonSearchResult } from './types';
import SearchAndAssignPerson from './SearchAndAssignPerson';
import { GET_ASSIGNED_PERSONS, SEARCH_EXTERNAL_USERS, UPDATE_PERSON_CLIENT_ROLES } from './queries';

const getPersonsFromQueryData = (data: any): Array<PersonSearchResult> =>
  (data && data.persons && data.persons.data) || [];

type SearchAndAssignPersonContainerProps = {
  clientId: string,
};

type SearchAndAssignPersonContainerState = {
  searchString: string,
  selectedPerson: any,
};

class SearchAndAssignPersonContainer extends React.Component<
  SearchAndAssignPersonContainerProps,
  SearchAndAssignPersonContainerState,
> {
  constructor() {
    super();
    this.state = {
      searchString: '',
      selectedPerson: null,
    };
  }

  handleSelectedPersonChanged = (selectedPerson: PersonSearchResult) => {
    this.setState({ selectedPerson });
  };

  handleSearchStringChanged = (searchString: string) => {
    this.setState({ searchString });
  };

  attachSelectedPersonToClient = (updatePersonClientRoles: Object => any) => {
    const { clientId } = this.props;
    const { selectedPerson } = this.state;

    if (!selectedPerson) {
      return;
    }

    const clientRoleIds = selectedPerson.defaultClientRoleId
      ? [selectedPerson.defaultClientRoleId]
      : ['1'];

    updatePersonClientRoles({
      variables: {
        clientId,
        personId: selectedPerson.id,
        clientRoleIds,
      },
    });
  };

  handlePersonAttached = () => {
    this.setState({ selectedPerson: null });
  };

  handleAttachPersonError = () =>
    toastError(
      'Unable to assign user',
      'An error occurred when trying to attach the user. It might work if you give it another try.',
    );

  handleRequestCreateNewPerson = () => {
    const { clientId } = this.props;
    openCreateOrEditPersonModal(clientId, null);
  };

  render() {
    const { clientId } = this.props;
    const { searchString, selectedPerson } = this.state;
    const shouldSearch = !!searchString && searchString.length >= 3;

    return (
      <Mutation
        mutation={UPDATE_PERSON_CLIENT_ROLES}
        onCompleted={this.handlePersonAttached}
        onError={this.handleAttachPersonError}
        refetchQueries={[{ query: GET_ASSIGNED_PERSONS, variables: { clientId } }]}
      >
        {updatePersonClientRoles => (
          <Query
            query={SEARCH_EXTERNAL_USERS}
            skip={!shouldSearch}
            variables={{ search: searchString }}
          >
            {({ data, loading }) => (
              <SearchAndAssignPerson
                attachSelectedPersonToClient={() =>
                  this.attachSelectedPersonToClient(updatePersonClientRoles)
                }
                searchString={searchString}
                isLoading={loading && shouldSearch}
                onRequestCreateNewPerson={this.handleRequestCreateNewPerson}
                onSearchStringChanged={this.handleSearchStringChanged}
                onSelectedPersonChanged={this.handleSelectedPersonChanged}
                persons={getPersonsFromQueryData(data)}
                selectedPerson={selectedPerson}
                submitLabel="Assign User"
              />
            )}
          </Query>
        )}
      </Mutation>
    );
  }
}

export default SearchAndAssignPersonContainer;
